import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, AlertTriangle, Loader2, ShieldCheck, Phone, CreditCard } from 'lucide-react';
import AdminSidebar from '../components/AdminSidebar';
import AdminTabs from '../components/AdminTabs';
import AdminLoadingScreen from '../components/AdminLoadingScreen';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

let toastCounter = 0;
function Toast({ toasts, onDismiss }) {
  if (!toasts.length) return null;
  return (
    <div className="fixed top-5 right-5 z-50 flex flex-col gap-3 pointer-events-none">
      {toasts.map(t => (
        <div key={t.id} className={`pointer-events-auto flex items-start gap-3 rounded-2xl px-5 py-4 shadow-lg text-sm font-medium max-w-sm ${t.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          {t.type === 'success' ? <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-green-600" /> : <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-red-600" />}
          <span className="flex-1">{t.message}</span>
          <button onClick={() => onDismiss(t.id)} className="ml-2 opacity-50 hover:opacity-100 text-base">×</button>
        </div>
      ))}
    </div>
  );
}

export default function AdminClientVerification() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'success') => {
    const id = ++toastCounter;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/clients/pending-kyc`, { credentials: 'include' });
        if (res.status === 401 || res.status === 403) { navigate('/login'); return; }
        if (!res.ok) throw new Error(`Error ${res.status}`);
        const data = await res.json();
        setClients(Array.isArray(data) ? data : []);
      } catch (err) {
        setFetchError(err.message || t('adminClientVerification.loadError', 'Error al cargar clientes'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const handleVerify = async (client) => {
    setActionLoading(prev => ({ ...prev, [client.user_id]: 'verifying' }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/clients/${client.user_id}/verify-kyc`, {
        method: 'POST', credentials: 'include'
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || `Error ${res.status}`); }
      setClients(prev => prev.filter(c => c.user_id !== client.user_id));
      addToast(t('adminClientVerification.verifiedOk', '✅ {{name}} verificado correctamente', { name: client.name }), 'success');
    } catch (err) {
      addToast(t('adminClientVerification.verifyError', 'Error al verificar {{name}}: {{message}}', { name: client.name, message: err.message }), 'error');
    } finally {
      setActionLoading(prev => ({ ...prev, [client.user_id]: null }));
    }
  };

  const handleReject = async (client) => {
    const reason = window.prompt(
      t('adminClientVerification.rejectPrompt', 'Motivo del rechazo (visible en el email al cliente):\nCliente: {{name}}', { name: client.name }),
      ''
    );
    if (reason === null) return;                    // cancelado
    if (reason.trim().length < 5) {
      addToast(t('adminClientVerification.rejectReasonTooShort', 'El motivo debe tener al menos 5 caracteres.'), 'error');
      return;
    }
    setActionLoading(prev => ({ ...prev, [client.user_id]: 'rejecting' }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/clients/${client.user_id}/reject-kyc`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason.trim() }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || `Error ${res.status}`); }
      setClients(prev => prev.filter(c => c.user_id !== client.user_id));
      addToast(t('adminClientVerification.rejectedOk', '{{name}} rechazado. Se le envió correo con el motivo.', { name: client.name }), 'success');
    } catch (err) {
      addToast(t('adminClientVerification.rejectError', 'Error al rechazar {{name}}: {{message}}', { name: client.name, message: err.message }), 'error');
    } finally {
      setActionLoading(prev => ({ ...prev, [client.user_id]: null }));
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  if (loading) return <AdminLoadingScreen message={t('adminClientVerification.loading', 'Cargando verificaciones KYC de clientes...')} />;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar />
      <div className="flex-1">
        <AdminTabs />
        <Toast toasts={toasts} onDismiss={dismissToast} />

        <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
          <BackButton to="/admin/dashboard" label={t('admin.back2Dashboard')} className="mb-8" />

          <div className="flex items-center gap-3 mb-2">
            <ShieldCheck className="w-8 h-8 text-[#134168]" />
            <h1 className="text-4xl font-heading font-bold text-[#134168]">{t('adminClientVerification.title', 'Verificación KYC de Clientes')}</h1>
          </div>
          <p className="text-gray-500 mb-8 text-sm">
            {t('adminClientVerification.pendingCount', '{{count}} cliente(s) pendiente(s) de verificación de identidad', { count: clients.length })}
          </p>

          {fetchError && (
            <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-2xl px-5 py-4">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p>{fetchError}</p>
            </div>
          )}

          {!fetchError && clients.length === 0 && (
            <div className="bg-white rounded-3xl p-12 shadow-sm text-center">
              <CheckCircle className="w-16 h-16 text-[#39b4aa] mx-auto mb-4" />
              <p className="text-gray-700 font-semibold text-lg">{t('adminClientVerification.allCaughtUp', 'Todo al día')}</p>
              <p className="text-gray-400 text-sm mt-1">{t('adminClientVerification.empty', 'No hay clientes pendientes de verificación KYC')}</p>
            </div>
          )}

          {clients.length > 0 && (
            <div className="grid grid-cols-1 gap-6">
              {clients.map(client => {
                const isVerifying = actionLoading[client.user_id] === 'verifying';
                const frontImg = client.document_image_front || (client.has_document ? `${BACKEND_URL}/api/admin/clients/${client.user_id}/document` : null);
                const backImg = client.document_image_back;
                return (
                  <div key={client.user_id} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      {/* Fotos de identidad */}
                      <div className="flex gap-2 flex-shrink-0">
                        {frontImg ? (
                          <a
                            href={frontImg}
                            target="_blank"
                            rel="noreferrer"
                            title="Ver frente del documento"
                            className="w-20 h-20 rounded-2xl overflow-hidden border-2 border-gray-200 hover:border-[#134168] transition-colors relative group bg-gray-100"
                          >
                            <img
                              src={frontImg}
                              alt="Frente"
                              className="w-full h-full object-cover"
                              crossOrigin="use-credentials"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                            <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[9px] text-center font-bold py-0.5">Frente</span>
                          </a>
                        ) : null}

                        {backImg ? (
                          <a
                            href={backImg}
                            target="_blank"
                            rel="noreferrer"
                            title="Ver reverso del documento"
                            className="w-20 h-20 rounded-2xl overflow-hidden border-2 border-gray-200 hover:border-[#134168] transition-colors relative group bg-gray-100"
                          >
                            <img
                              src={backImg}
                              alt="Reverso"
                              className="w-full h-full object-cover"
                              crossOrigin="use-credentials"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                            <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[9px] text-center font-bold py-0.5">Reverso</span>
                          </a>
                        ) : null}

                        {!frontImg && !backImg && (
                          <div className="w-16 h-16 bg-[#134168] rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0" title={t('adminClientVerification.noDocumentTitle', 'Este cliente solo envió el número del documento (aún no subió imagen)')}>
                            {(client.name || '?')[0].toUpperCase()}
                          </div>
                        )}
                      </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-lg text-gray-900">{client.name || t('adminClientVerification.noName', 'Sin nombre')}</h3>
                      <p className="text-sm text-gray-600">{client.email}</p>
                      <div className="flex flex-wrap gap-3 mt-2">
                        {client.phone_whatsapp && (
                          <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-lg">
                            <Phone className="w-3 h-3" /> {client.phone_whatsapp}
                          </span>
                        )}
                        {client.document_id && (
                          <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-lg">
                            <CreditCard className="w-3 h-3" /> {client.document_id}
                          </span>
                        )}
                        <span className="text-xs text-gray-400">{t('adminClientVerification.registeredLabel', 'Registrado: {{date}}', { date: formatDate(client.created_at) })}</span>
                      </div>
                    </div>

                    <div className="flex gap-3 flex-shrink-0">
                      <button
                        onClick={() => handleVerify(client)}
                        disabled={!!actionLoading[client.user_id]}
                        className="flex items-center gap-2 bg-green-500 text-white px-5 py-2.5 rounded-full font-bold hover:bg-green-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        {isVerifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                        {isVerifying ? t('adminClientVerification.verifying', 'Verificando...') : t('adminClientVerification.verifyBtn', 'Verificar identidad')}
                      </button>
                      <button
                        onClick={() => handleReject(client)}
                        disabled={!!actionLoading[client.user_id]}
                        className="flex items-center gap-2 bg-white border border-red-300 text-red-600 px-5 py-2.5 rounded-full font-bold hover:bg-red-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        {actionLoading[client.user_id] === 'rejecting' ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                        {actionLoading[client.user_id] === 'rejecting' ? t('adminClientVerification.rejecting', 'Rechazando...') : t('adminClientVerification.rejectBtn', 'Rechazar')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
